import { Check, Globe } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { FeatureTiles } from '@/features/site-setup/FeatureTiles';
import {
  parseLanguages,
  setupSite,
  SITE_FEATURES,
  type SiteFeature,
} from '@/features/site-setup/siteSetup';
import type { SiteBody, TenantBody } from '@/shared/api/generated/model';
import { sitesCreate, sitesUpdate } from '@/shared/api/generated/sites/sites';
import { ApiError } from '@/shared/api/http';
import { cx } from '@/shared/lib/cx';
import { slugify } from '@/shared/lib/slugify';
import { Button } from '@/shared/ui/Button';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { Select, SelectItem } from '@/shared/ui/Select';
import { toast } from '@/shared/ui/Toast';

import styles from './SiteFormDialog.module.scss';

export interface SiteFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Заполнено — правим существующий, пусто — заводим новый. */
  site?: SiteBody | undefined;
  tenants: TenantBody[];
  /** Какой тенант подставить в новом сайте. Пусто — выбирает человек. */
  defaultTenantID?: string;
  onDone: () => void;
}

/** Этапы нового сайта — по ресерчу 13: кто и как называется, что на сайте, где он. */
const STEPS = [
  {
    title: 'Основное',
    description: 'Вместе с сайтом заведутся окружения Production и Development.',
  },
  { title: 'Что будет на сайте', description: 'Отметьте нужное — в меню появится только оно.' },
  { title: 'Языки и адреса', description: 'Всё можно поменять потом в настройках сайта.' },
] as const;

const LAST_STEP = STEPS.length - 1;

/**
 * Форма сайта: одна на создание и на переименование. Новый сайт — в три этапа
 * с видимым ходом сверху: название, возможности, языки и адреса (ресерч 13).
 */
export function SiteFormDialog({
  open,
  onOpenChange,
  site,
  tenants,
  defaultTenantID = '',
  onDone,
}: SiteFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <Body
          site={site}
          tenants={tenants}
          defaultTenantID={defaultTenantID}
          onOpenChange={onOpenChange}
          onDone={onDone}
        />
      )}
    </Dialog>
  );
}

function Body({
  site,
  tenants,
  defaultTenantID,
  onOpenChange,
  onDone,
}: Omit<SiteFormDialogProps, 'open' | 'defaultTenantID'> & { defaultTenantID: string }) {
  const editing = site !== undefined;

  const [tenantID, setTenantID] = useState(site?.tenant_id ?? defaultTenantID);
  const [name, setName] = useState(site?.name ?? '');
  const [slug, setSlug] = useState(site?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(editing);

  const [step, setStep] = useState(0);
  // Направление нужно движению: вперёд экран въезжает справа, назад — слева.
  const [direction, setDirection] = useState<'forward' | 'back'>('forward');
  const [features, setFeatures] = useState<SiteFeature[]>([]);
  const [languages, setLanguages] = useState('ru');
  const [developmentURL, setDevelopmentURL] = useState('');
  const [productionURL, setProductionURL] = useState('');

  const [nameError, setNameError] = useState<string>();
  const [slugError, setSlugError] = useState<string>();
  const [tenantError, setTenantError] = useState<string>();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const goTo = (next: number) => {
    setDirection(next > step ? 'forward' : 'back');
    setStep(next);
  };

  const fail = (cause: unknown) => {
    // 409 означает занятое короткое имя — это ошибка поля, и показывать её
    // надо у него, на первом этапе, а не общим сообщением внизу окна.
    if (cause instanceof ApiError && cause.status === 409) {
      setSlugError(cause.detail);
      goTo(0);
    } else if (cause instanceof ApiError) {
      setError(cause.detail);
    } else {
      setError('Не удалось сохранить. Попробуйте ещё раз.');
    }
  };

  /** Первый этап проверяем до перехода: иначе ошибка всплыла бы только в конце. */
  const validateBasics = () => {
    let ok = true;

    if (!editing && tenantID === '') {
      setTenantError('Выберите тенанта');
      ok = false;
    }

    if (name.trim() === '') {
      setNameError('Введите название');
      ok = false;
    }

    if (slug.trim() === '') {
      setSlugError('Введите короткое имя');
      ok = false;
    }

    return ok;
  };

  const create = async () => {
    const response = await sitesCreate({ tenant_id: tenantID, name: name.trim(), slug });

    if (response.status !== 201) {
      throw new Error('Неожиданный ответ на создание сайта');
    }

    // Сайт уже есть: недоделанное ниже поправят в настройках, а не создадут
    // второй сайт повторным нажатием.
    const failed = await setupSite(response.data.id, response.data.etag, {
      features,
      languages: parseLanguages(languages),
      developmentURL,
      productionURL,
    });

    if (failed.length === 0) {
      toast.success('Сайт создан');
    } else {
      toast.error(
        `Сайт создан, но не сохранилось: ${failed.join('; ')}. Поправьте в настройках сайта.`,
      );
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    setNameError(undefined);
    setSlugError(undefined);
    setTenantError(undefined);

    if (step === 0 && !validateBasics()) {
      return;
    }

    if (!editing && step < LAST_STEP) {
      goTo(step + 1);

      return;
    }

    setBusy(true);

    const request = editing
      ? sitesUpdate(site.id, { name: name.trim(), slug }, { 'If-Match': site.etag }).then(() => {
          toast.success('Изменения сохранены');
        })
      : create();

    request
      .then(() => {
        onDone();
        onOpenChange(false);
      })
      .catch(fail)
      .finally(() => {
        setBusy(false);
      });
  };

  if (editing) {
    return (
      <DialogContent
        title="Переименовать сайт"
        size="sm"
        dismissible={false}
        footer={
          <>
            <Button
              variant="outline"
              tone="neutral"
              type="button"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Отмена
            </Button>
            <Button type="submit" form="site-form" loading={busy}>
              Сохранить
            </Button>
          </>
        }
      >
        <form id="site-form" className={styles['form']} onSubmit={handleSubmit} noValidate>
          {basicsStep()}
          {error !== undefined && <p className={styles['error']}>{error}</p>}
        </form>
      </DialogContent>
    );
  }

  const current = STEPS[step] ?? STEPS[0];

  return (
    <DialogContent
      title="Новый сайт"
      description={current.description}
      size="md"
      dismissible={false}
      footer={
        <div className={styles['footer']}>
          <span className={styles['footer__count']}>
            Шаг {step + 1} из {STEPS.length}
          </span>
          <div className={styles['footer__actions']}>
            <Button
              variant="outline"
              tone="neutral"
              type="button"
              onClick={() => {
                if (step > 0) {
                  goTo(step - 1);
                } else {
                  onOpenChange(false);
                }
              }}
            >
              {step > 0 ? 'Назад' : 'Отмена'}
            </Button>
            <Button type="submit" form="site-form" loading={busy}>
              {step < LAST_STEP ? 'Дальше' : 'Создать сайт'}
            </Button>
          </div>
        </div>
      }
    >
      <ol className={styles['stepper']} aria-label="Этапы">
        {STEPS.map((item, index) => {
          const state = index < step ? 'done' : index === step ? 'current' : 'todo';

          return (
            <li
              key={item.title}
              className={cx(styles['stepper__item'], styles[`stepper__item--${state}`])}
            >
              <button
                type="button"
                className={styles['stepper__button']}
                // Вернуться можно на пройденный этап, перепрыгнуть вперёд — нет:
                // первый этап проверяется перед уходом с него.
                disabled={index >= step}
                aria-current={state === 'current' ? 'step' : undefined}
                onClick={() => {
                  goTo(index);
                }}
              >
                <span className={styles['stepper__dot']}>
                  {state === 'done' ? <Check size={14} strokeWidth={3} /> : index + 1}
                </span>
                <span className={styles['stepper__title']}>{item.title}</span>
              </button>
            </li>
          );
        })}
      </ol>

      <form id="site-form" onSubmit={handleSubmit} noValidate>
        <div
          key={step}
          className={cx(styles['form'], styles['panel'], styles[`panel--${direction}`])}
        >
          {step === 0 && basicsStep()}
          {step === 1 && featuresStep()}
          {step === 2 && placesStep()}
          {error !== undefined && <p className={styles['error']}>{error}</p>}
        </div>
      </form>
    </DialogContent>
  );

  // Обычные функции, не компоненты: компонент, объявленный в рендере, React
  // пересоздавал бы на каждое нажатие — поле теряло бы фокус.
  function basicsStep() {
    return (
      <>
        {!editing && (
          <Field label="Тенант" required error={tenantError}>
            <Select
              value={tenantID}
              onValueChange={(value) => {
                setTenantID(value);
                setTenantError(undefined);
              }}
              placeholder="Выберите тенанта"
            >
              {tenants.map((tenant) => (
                <SelectItem key={tenant.id} value={tenant.id} description={tenant.slug}>
                  {tenant.name}
                </SelectItem>
              ))}
            </Select>
          </Field>
        )}

        <Field label="Название" required error={nameError}>
          <Input
            value={name}
            autoFocus
            placeholder="Портал"
            onChange={(event) => {
              setName(event.target.value);
              setNameError(undefined);

              if (!slugTouched) {
                setSlug(slugify(event.target.value));
                setSlugError(undefined);
              }
            }}
          />
        </Field>

        <Field
          label="Короткое имя"
          required
          error={slugError}
          hint="Уникально внутри тенанта. Строчные латинские буквы, цифры и дефис."
        >
          <Input
            mono
            value={slug}
            placeholder="portal"
            onChange={(event) => {
              setSlugTouched(true);
              setSlug(event.target.value);
              setSlugError(undefined);
            }}
          />
        </Field>
      </>
    );
  }

  function featuresStep() {
    return (
      <>
        <FeatureTiles value={features} onChange={setFeatures} />
        <p className={styles['note']}>
          Ничего не нужно — оставьте пустым: модели и страницы есть у любого сайта.
        </p>
      </>
    );
  }

  function placesStep() {
    const parsed = parseLanguages(languages);
    const chosen = SITE_FEATURES.filter((f) => features.includes(f.key)).map((f) => f.title);

    return (
      <>
        <Field label="Языки" hint="Через запятую, первый — основной: ru, en, kk.">
          <Input
            mono
            value={languages}
            onChange={(event) => {
              setLanguages(event.target.value);
            }}
          />
        </Field>
        <div className={styles['langs']} aria-live="polite">
          {parsed.map((code, index) => (
            <span key={code} className={cx(styles['lang'], index === 0 && styles['lang--main'])}>
              {code}
              {index === 0 && <span className={styles['lang__note']}>основной</span>}
            </span>
          ))}
        </div>

        <div className={styles['row']}>
          <Field label="Адрес Development" hint="Тестовый стенд — для предпросмотра.">
            <Input
              mono
              value={developmentURL}
              placeholder="https://dev.example.ru"
              onChange={(event) => {
                setDevelopmentURL(event.target.value);
              }}
            />
          </Field>
          <Field label="Адрес Production" hint="Боевой сайт. Можно указать потом.">
            <Input
              mono
              value={productionURL}
              placeholder="https://example.ru"
              onChange={(event) => {
                setProductionURL(event.target.value);
              }}
            />
          </Field>
        </div>

        <dl className={styles['summary']}>
          <div className={styles['summary__head']}>
            <Globe size={16} />
            <span>
              {name.trim()} <span className={styles['summary__slug']}>{slug}</span>
            </span>
          </div>
          <div className={styles['summary__row']}>
            <dt>Окружения</dt>
            <dd>Production и Development</dd>
          </div>
          <div className={styles['summary__row']}>
            <dt>На сайте</dt>
            <dd>{chosen.length > 0 ? chosen.join(', ') : 'Модели и страницы'}</dd>
          </div>
          <div className={styles['summary__row']}>
            <dt>Языки</dt>
            <dd>{parsed.join(', ')}</dd>
          </div>
        </dl>
      </>
    );
  }
}
